/**
 * @description SO route wrapper — pack light shell or LynxPay chrome (LYN-360, LYN-361)
 *
 * Key features:
 * - Pack flag from GET /features/pack-chrome
 * - Pack-auth pages mount PackSOLayout (no AppHeader / dark rail / AppFooter)
 * - LynxPay SO keeps BaseSidebarLayout + SOSidebar
 * - Unresolved pack flag holds a pack-sized shell or last-known pack hint
 *
 * @dependencies PackChromeContext, PackSOLayout, PackResolvingShell, BaseSidebarLayout
 *
 * @notes CompanyProvider + PermissionProvider stay so OnboardingModal can reuse
 *   the 356 writer. Never mount LynxPay chrome while pack-chrome is in flight.
 */

import React from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { CompanyProvider } from '../../../contexts/CompanyContext';
import { PermissionProvider } from '../../../contexts/PermissionContext';
import { PackChromeProvider, usePackChrome } from '../../../contexts/PackChromeContext';
import BaseSidebarLayout from '../shared/BaseSidebarLayout';
import SOSidebar from './SOSidebar';
import PackOnboardingHost from './PackOnboardingHost';
import PackResolvingShell from './PackResolvingShell';
import PackSOLayout from './PackSOLayout';
import { readPackChromeHint } from '../../../utils/packChromeHint';

export function SOLayoutShell() {
  const { activeCompanyId, user } = useAuth();
  const { towtechSoChrome, loading } = usePackChrome();

  if (loading) {
    if (towtechSoChrome || readPackChromeHint(user?.id) === true) {
      return <PackSOLayout outletKey={activeCompanyId} />;
    }
    return <PackResolvingShell />;
  }

  if (towtechSoChrome) {
    return <PackSOLayout outletKey={activeCompanyId} />;
  }

  return (
    <BaseSidebarLayout
      SidebarContent={SOSidebar}
      storageKey="soSidebarOpen"
    >
      <>
        <PackOnboardingHost />
        <Outlet key={activeCompanyId} />
      </>
    </BaseSidebarLayout>
  );
}

const SOLayout = () => (
  <PackChromeProvider>
    <CompanyProvider>
      <PermissionProvider>
        <SOLayoutShell />
      </PermissionProvider>
    </CompanyProvider>
  </PackChromeProvider>
);

export default SOLayout;
